import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import _range from 'lodash/range';
import { useTranslation } from 'react-i18next';

import { HoursOfOperation } from './hours-of-operation/hours-of-operation';
import { ProfileOptions } from './profile-options';
import { RequiredDocs } from './required-docs';
import * as ClubFormCtx from '../club-form-context';
import { FormTextareaField } from '../../../../common/form/form-textarea-field';
import { FormNumberField } from '../../../../common/form/form-number-field';
import { FormSelectField } from '../../../../common/form/form-select-field';
import { FormTextField } from '../../../../common/form/form-text-field';
import * as ClubValidations from '../../../../../helpers/validations/club-validation';
import { useUser, useBusiness } from '../../../../../graphql/graph-hooks';
import classNames from 'classnames';

const AdditionalInfoForm = ({ isDisabled }) => {
  const { t } = useTranslation();
  const { admin } = useUser();
  const { free_agent_search_enabled: freeAgentSearchEnabled } = useBusiness();
  const {
    dispatch,
    state: { form, errors },
  } = ClubFormCtx.useClubFormCtx();
  const handleChange = useCallback(
    (e) => dispatch(ClubFormCtx.setClubFormField(e.target.name, e.target.value)),
    [dispatch]
  );
  return (
    <div className='grid grid-cols-6 gap-4'>
      <div className='col-span-6 mt-8'>
        <HoursOfOperation isDisabled={isDisabled} />
      </div>
      <div className='col-span-1'>
        <FormNumberField
          id='club_form_sqFootage'
          gridArea='size'
          name='squareFootage'
          label={t('clubPage.square_footage')}
          min={1}
          error={errors.squareFootage}
          isDisabled={isDisabled}
          value={form.squareFootage}
          handleBlur={({ target: { name, value } }) => {
            dispatch(ClubFormCtx.setClubFormField(name, value.trim()));
            dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateInteger(value.trim())));
          }}
          handleChange={({ target: { name, value } }) => {
            dispatch(ClubFormCtx.setClubFormField(name, value));
            dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateInteger(value)));
          }}
        />
      </div>
      <div className='col-span-1'>
        <FormNumberField
          id='club_form_stageCount'
          gridArea='stages'
          min={1}
          name='numberOfStages'
          label={t('clubPage.num_stages')}
          isDisabled={isDisabled}
          value={form.numberOfStages}
          error={errors.numberOfStages}
          handleBlur={({ target: { name, value } }) => {
            dispatch(ClubFormCtx.setClubFormField(name, value.trim()));
            dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateInteger(value.trim())));
          }}
          handleChange={({ target: { name, value } }) => {
            dispatch(ClubFormCtx.setClubFormField(name, value));
            dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateInteger(value)));
          }}
        />
      </div>
      <div className='col-span-4'>
        <FormTextField
          id='club_form_regLink'
          gridArea='regLink'
          name='specialRequirementsLink'
          label={t('clubPage.registration_link')}
          error={errors.specialRequirementsLink}
          isDisabled={isDisabled}
          value={form.specialRequirementsLink}
          handleBlur={({ target: { name, value } }) => {
            dispatch(ClubFormCtx.setClubFormField(name, value.trim()));
            dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateClubUrl(value.trim())));
          }}
          handleChange={({ target: { name, value } }) => {
            dispatch(ClubFormCtx.setClubFormField(name, value));
            dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateClubUrl(value)));
          }}
        />
      </div>
      {admin && (
        <>
          <div className='col-span-2'>
            <FormNumberField
              id='club_form_minDancersForFreeAgentSearch'
              name='minDancersForFreeAgentSearch'
              label={t('clubPage.min_dancers_free_agent_search')}
              min={0}
              error={errors.minDancersForFreeAgentSearch}
              isDisabled={isDisabled}
              value={form.minDancersForFreeAgentSearch}
              handleBlur={({ target: { name, value } }) => {
                dispatch(ClubFormCtx.setClubFormField(name, value.trim()));
                dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateInteger(value.trim())));
              }}
              handleChange={({ target: { name, value } }) => {
                dispatch(ClubFormCtx.setClubFormField(name, value));
                dispatch(ClubFormCtx.setClubFormError(name, ClubValidations.validateInteger(value)));
              }}
            />
          </div>
          <div className='col-span-2'>
            <FormNumberField
              id='club_form_dancerCount'
              gridArea='dancerCount'
              name='dancerCount'
              label={t('clubPage.current_dancer_count')}
              min={0}
              error={errors.dancerCount}
              isDisabled={true}
              value={form.dancerCount}
              handleBlur={() => {}}
              handleChange={() => {}}
            />
          </div>
          <div
            className={classNames([
              'col-span-2 text-lg text-bold pt-7',
              freeAgentSearchEnabled ? 'text-green-400' : 'text-red-400',
            ])}
          >
            {freeAgentSearchEnabled ? t('clubPage.free_agent_enabled') : t('clubPage.free_agent_disabled')}
          </div>
        </>
      )}
      <div className='col-span-3'>
        <ProfileOptions isDisabled={isDisabled} />
      </div>
      <div className='col-span-3'>
        <RequiredDocs isDisabled={isDisabled} />
      </div>
      <div className='col-span-3'>
        <FormTextareaField
          id='club_form_audition'
          gridArea='audition'
          name='auditionInfo'
          label={t('clubPage.audition_notes')}
          isDisabled={isDisabled}
          error={errors.auditionInfo}
          value={form.auditionInfo}
          handleChange={handleChange}
        />
      </div>
      <div className='col-span-3'>
        <FormTextareaField
          id='club_form_notes'
          gridArea='notes'
          name='notes'
          label={t('clubPage.additional_comments')}
          isDisabled={isDisabled}
          error={errors.notes}
          value={form.notes}
          handleChange={handleChange}
        />
      </div>
    </div>
  );
};

AdditionalInfoForm.displayName = 'AdditionalInfoForm';

AdditionalInfoForm.propTypes = {
  isDisabled: PropTypes.bool.isRequired,
};

export { AdditionalInfoForm };
